import React, {
  CSSProperties,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import {
  Box,
  Divider,
  Grid,
  Stack,
  TableCell,
  TableRow,
  useTheme,
} from "@mui/material";
import "./DashboardSkeleton.scss";

export interface SkeletonBlockProps {
  width?: number | string;
  height?: number | string;
  radius?: number | string;
  style?: CSSProperties;
  className?: string;
}

export function SkeletonBlock({
  width = "100%",
  height = 16,
  radius = 8,
  style,
  className = "",
}: SkeletonBlockProps) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  return (
    <span
      className={`dashboard-skeleton-block${isDark ? " dashboard-skeleton-block--dark" : ""} ${className}`.trim()}
      style={{
        width,
        height,
        borderRadius: radius,
        ...style,
      }}
      aria-hidden="true"
    />
  );
}

export function SummaryCardValueSkeleton() {
  return (
    <div
      className="dashboard-skeleton-summary"
      aria-busy="true"
      aria-live="polite"
    >
      <SkeletonBlock width={140} height={28} />
      <SkeletonBlock width={110} height={14} />
      <SkeletonBlock width={70} height={10} />
    </div>
  );
}

export function GstCardValueSkeleton() {
  return (
    <div
      className="dashboard-skeleton-gst"
      aria-busy="true"
      aria-live="polite"
    >
      <SkeletonBlock width={140} height={28} />
      <SkeletonBlock width={70} height={10} />
    </div>
  );
}

const AGING_COLUMNS = [
  { showOverdue: false, labelWidth: 90 },
  { showOverdue: true, labelWidth: 80 },
  { showOverdue: true, labelWidth: 80 },
  { showOverdue: true, labelWidth: 80 },
  { showOverdue: true, labelWidth: 70 },
];

export function AgingStripSkeleton() {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  return (
    <div
      className="dashboard-skeleton-aging"
      aria-busy="true"
      aria-live="polite"
    >
      <div className="dashboard-skeleton-aging__total">
        <SkeletonBlock width={180} height={30} />
      </div>
      <div
        className={`dashboard-skeleton-aging__strip${isDark ? " dashboard-skeleton-aging__strip--dark" : ""}`}
        style={{
          paddingLeft: 0,
          paddingRight: 0,
          marginLeft: 20,
          marginRight: 20,
          marginBottom: 20,
        }}
      >
        {AGING_COLUMNS.map((col, index) => (
          <div key={index} className="dashboard-skeleton-aging__col">
            {col.showOverdue && <SkeletonBlock width={50} height={10} />}
            <SkeletonBlock width={col.labelWidth} height={14} />
            <SkeletonBlock width={90} height={18} />
          </div>
        ))}
      </div>
    </div>
  );
}

const BAR_HEIGHTS = [55, 78, 42, 90, 65, 48, 85, 70, 38, 60];

export function BarChartSkeleton() {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  return (
    <div
      className="dashboard-skeleton-bar-chart"
      aria-busy="true"
      aria-live="polite"
    >
      <div className="dashboard-skeleton-bar-chart__plot">
        <div className="dashboard-skeleton-bar-chart__y-labels">
          {[0, 1, 2, 3, 4].map((i) => (
            <SkeletonBlock key={i} width={32} height={10} />
          ))}
        </div>
        <div className="dashboard-skeleton-bar-chart__grid">
          {[0, 1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className={`dashboard-skeleton-bar-chart__grid-line${isDark ? " dashboard-skeleton-bar-chart__grid-line--dark" : ""}`}
            />
          ))}
        </div>
        <div className="dashboard-skeleton-bar-chart__bars">
          {BAR_HEIGHTS.map((h, i) => (
            <SkeletonBlock
              key={i}
              className="dashboard-skeleton-bar-chart__bar"
              width="100%"
              height={`${h}%`}
              radius="6px 6px 0 0"
              style={{ alignSelf: "flex-end" }}
            />
          ))}
        </div>
      </div>
      <div className="dashboard-skeleton-bar-chart__x-labels">
        {BAR_HEIGHTS.map((_, i) => (
          <div key={i} className="dashboard-skeleton-bar-chart__x-label">
            <SkeletonBlock width={28} height={10} />
          </div>
        ))}
      </div>
    </div>
  );
}

const LEGEND_WIDTHS = [120, 90, 140, 100, 80];

export function DonutChartSkeleton() {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  return (
    <div
      className="dashboard-skeleton-donut"
      aria-busy="true"
      aria-live="polite"
    >
      <div
        className={`dashboard-skeleton-donut__ring${isDark ? " dashboard-skeleton-donut__ring--dark" : ""}`}
      />
      <div className="dashboard-skeleton-donut__legend">
        {LEGEND_WIDTHS.map((w, i) => (
          <div key={i} className="dashboard-skeleton-donut__legend-row">
            <SkeletonBlock width={12} height={12} radius={3} />
            <SkeletonBlock width={w} height={12} />
          </div>
        ))}
      </div>
    </div>
  );
}

export function BankCashSkeleton() {
  return (
    <div
      className="dashboard-skeleton-bank"
      aria-busy="true"
      aria-live="polite"
    >
      {[0, 1].map((index) => (
        <React.Fragment key={index}>
          <Box className="dashboard-skeleton-bank__row">
            <SkeletonBlock width={110} height={16} />
            <SkeletonBlock width={90} height={16} />
          </Box>
          {index === 0 && <Divider />}
        </React.Fragment>
      ))}
    </div>
  );
}

export interface TableRowsSkeletonProps {
  rows?: number;
  /** Fixed column count. When omitted, counts visible header cells from the parent table. */
  columns?: number;
  /** Used until thead is measured (or when measurement fails). */
  fallbackColumns?: number;
}

function countVisibleTableColumns(table: HTMLTableElement): number {
  const headerRow =
    table.querySelector("thead tr") ||
    table.querySelector("tr");
  if (!headerRow) return 0;

  let count = 0;
  Array.from(headerRow.children).forEach((cell) => {
    const tag = cell.tagName;
    if (tag !== "TH" && tag !== "TD") return;
    const style = window.getComputedStyle(cell);
    if (style.display === "none" || style.visibility === "hidden") return;
    const span = Number((cell as HTMLTableCellElement).colSpan) || 1;
    count += span;
  });
  return count;
}

/**
 * Full-width table body skeleton. Column count follows the UI table headers
 * (visible thead cells), so rows span the entire line for any list screen.
 */
export function TableRowsSkeleton({
  rows = 5,
  columns,
  fallbackColumns = 12,
}: TableRowsSkeletonProps) {
  const anchorRef = useRef<HTMLTableRowElement>(null);
  const [colCount, setColCount] = useState(
    typeof columns === "number" && columns > 0 ? columns : fallbackColumns,
  );

  useLayoutEffect(() => {
    if (typeof columns === "number" && columns > 0) {
      setColCount(columns);
      return;
    }

    const rowEl = anchorRef.current;
    if (!rowEl) return;
    const table = rowEl.closest("table");
    if (!table) return;

    const syncColumns = () => {
      const next = countVisibleTableColumns(table);
      if (next > 0) setColCount(next);
    };

    syncColumns();

    const observer = new ResizeObserver(syncColumns);
    observer.observe(table);
    const thead = table.querySelector("thead");
    if (thead) observer.observe(thead);

    return () => observer.disconnect();
  }, [columns, rows]);

  return (
    <>
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <TableRow
          key={rowIndex}
          ref={rowIndex === 0 ? anchorRef : undefined}
          className="dashboard-skeleton-table-row"
          aria-busy="true"
        >
          {Array.from({ length: colCount }).map((_, colIndex) => (
            <TableCell
              key={colIndex}
              sx={{
                py: 1.5,
                width: colIndex === 0 ? 48 : undefined,
              }}
            >
              <SkeletonBlock
                className="dashboard-skeleton-table-cell-fill"
                width="100%"
                height={16}
                radius={6}
              />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  );
}

/** Tab-pill shaped amount placeholder (matches ListTabsSkeleton style). */
export function InlineAmountSkeleton({ width = 110 }: { width?: number }) {
  return <SkeletonBlock width={width} height={32} radius={10} />;
}

/**
 * While loading, replaces children with a tab-like filled skeleton pill.
 * Wrap the whole amount Box (including inner-tabs) so the bordered empty
 * shell is not shown during load.
 */
export function BoundAmount({
  loading,
  children,
  width = 110,
}: {
  loading: boolean;
  children: React.ReactNode;
  width?: number;
}) {
  if (loading) return <InlineAmountSkeleton width={width} />;
  return <>{children}</>;
}

export function ListTabsSkeleton({ tabs = 8 }: { tabs?: number }) {
  return (
    <div
      className="dashboard-skeleton-list-tabs"
      aria-busy="true"
      aria-live="polite"
    >
      {Array.from({ length: tabs }).map((_, index) => (
        <SkeletonBlock
          key={index}
          width={88 + (index % 3) * 14}
          height={32}
          radius={10}
        />
      ))}
    </div>
  );
}

export function ListPaginationSkeleton() {
  return (
    <Grid
      container
      paddingTop={1}
      alignItems="center"
      justifyContent="center"
      aria-busy="true"
    >
      <Grid item xs={12} sm={6} className="pagination-label">
        <SkeletonBlock width={120} height={16} />
      </Grid>
      <Grid item xs={12} sm={6}>
        <Stack component="div" alignItems="end" justifyContent="center">
          <SkeletonBlock width={180} height={32} radius={8} />
        </Stack>
      </Grid>
    </Grid>
  );
}

/** Shared tabs slot: skeleton while loading, real tabs otherwise. */
export function ListTabsOrContent({
  loading,
  tabs = 5,
  children,
}: {
  loading: boolean;
  tabs?: number;
  children: React.ReactNode;
}) {
  if (loading) return <ListTabsSkeleton tabs={tabs} />;
  return <>{children}</>;
}

/** Shared pagination slot: skeleton while loading, hidden when empty. */
export function ListPaginationOrContent({
  loading,
  hasRows,
  children,
}: {
  loading: boolean;
  hasRows: boolean;
  children: React.ReactNode;
}) {
  if (!loading && !hasRows) return null;
  if (loading) return <ListPaginationSkeleton />;
  return <>{children}</>;
}

export interface FormFieldSkeletonProps {
  labelWidth?: number | string;
  fieldHeight?: number | string;
}

/** Label + input-shaped block for form pages. */
export function FormFieldSkeleton({
  labelWidth = 80,
  fieldHeight = 40,
}: FormFieldSkeletonProps) {
  return (
    <div className="form-page-skeleton__field" aria-busy="true">
      <SkeletonBlock width={labelWidth} height={12} />
      <SkeletonBlock width="100%" height={fieldHeight} radius={8} />
    </div>
  );
}

export interface FormPageSkeletonProps {
  sections?: number;
  fieldsPerSection?: number;
  tableRows?: number;
  showTotals?: boolean;
  showFooter?: boolean;
}

/**
 * Generic form-page skeleton (Sale Save and similar screens).
 * Keeps page chrome outside; only form body is replaced by this.
 */
export function FormPageSkeleton({
  sections = 3,
  fieldsPerSection = 4,
  tableRows = 4,
  showTotals = true,
  showFooter = true,
}: FormPageSkeletonProps) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const sectionClass = `form-page-skeleton__section${isDark ? " form-page-skeleton__section--dark" : ""}`;
  const headClass = `form-page-skeleton__table-head${isDark ? " form-page-skeleton__table-head--dark" : ""}`;

  return (
    <div
      className="form-page-skeleton dashboard-fade-in"
      aria-busy="true"
      aria-live="polite"
      role="status"
    >
      {Array.from({ length: sections }).map((_, sIdx) => (
        <div key={sIdx} className={sectionClass}>
          <div className="form-page-skeleton__section-title">
            <SkeletonBlock width={140} height={14} />
          </div>
          <div className="form-page-skeleton__grid">
            {Array.from({ length: fieldsPerSection }).map((_, fIdx) => (
              <FormFieldSkeleton
                key={fIdx}
                labelWidth={60 + ((fIdx * 17) % 40)}
              />
            ))}
          </div>
        </div>
      ))}

      <div className={sectionClass}>
        <div className="form-page-skeleton__section-title">
          <SkeletonBlock width={100} height={14} />
        </div>
        <div className={headClass}>
          <SkeletonBlock width={120} height={12} />
          <SkeletonBlock width={70} height={12} />
          <SkeletonBlock width={70} height={12} />
          <SkeletonBlock width={70} height={12} />
          <SkeletonBlock width={80} height={12} />
        </div>
        {Array.from({ length: tableRows }).map((_, rIdx) => (
          <div key={rIdx} className="form-page-skeleton__table-row">
            <SkeletonBlock width="28%" height={36} radius={8} />
            <SkeletonBlock width="12%" height={36} radius={8} />
            <SkeletonBlock width="12%" height={36} radius={8} />
            <SkeletonBlock width="12%" height={36} radius={8} />
            <SkeletonBlock width="14%" height={36} radius={8} />
          </div>
        ))}
        {showTotals && (
          <div className="form-page-skeleton__totals">
            <SkeletonBlock width={180} height={14} />
            <SkeletonBlock width={160} height={14} />
            <SkeletonBlock width={200} height={22} />
          </div>
        )}
      </div>

      {showFooter && (
        <div className="form-page-skeleton__footer">
          <SkeletonBlock width={100} height={36} radius={8} />
          <SkeletonBlock width={120} height={36} radius={8} />
          <SkeletonBlock width={130} height={36} radius={8} />
        </div>
      )}
    </div>
  );
}
